/**
 * SqlButton — przycisk SQL na formularzu rekordu (klocek układu `sql_button`).
 *
 * Czysto prezentacyjny + obsługa kliknięcia; stan zadań rekordu (`active`, `lastRun`) dostaje od
 * strony z `useRecordProcedureRuns`. Stany (makieta zatwierdzona przez właściciela):
 *   - gotowy                — aktywny przycisk; gdy TA procedura ma poprzednie uruchomienie, obok niego
 *                             odznaka + godzina + tekst wyniku (Zakończony / Błąd / Przerwane);
 *   - wykonuję              — od kliknięcia do odpowiedzi 202 (a przy formularzu w edycji także na czas
 *                             zapisu przed zadaniem): przycisk z kręciołkiem + „Wykonuję…”;
 *   - czeka / trwa          — zadanie TEJ procedury w kolejce lub w toku: odznaka + „od HH:MM” (+ osoba);
 *                             przy „Trwa” podpowiedź o blokadzie rekordu;
 *   - zadanie innego przycisku — na rekordzie naraz jedno zadanie, więc przycisk jest nieaktywny;
 *   - bez prawa edycji      — wyszarzony, bez podpowiedzi (decyzja właściciela).
 *
 * Zapis przed uruchomieniem: gdy strona podaje bramkę (`beforeStart`, formularz w edycji), klik najpierw
 * wykonuje zwykły zapis, a zadanie rusza dopiero po jego sukcesie (błąd zapisu = zadanie NIE startuje —
 * bramka po prostu nie woła funkcji startu). Bez bramki (podgląd) zadanie rusza od razu. Brak potwierdzenia.
 */

import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, type BadgeTone } from '@/components/Badge';
import { Button } from '@/components/Button';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';
import { runResultText } from '@/lib/record-procedures/run-texts';
import {
  formatRunClock,
  isActiveRunStatus,
  type RecordProcedureRun,
  type RecordRunStatus,
  runFinishedIso,
  runSinceIso,
} from '@/lib/record-procedures/runs-api';

/** Odznaka wg makiety: czeka / trwa — neutralna, zakończony — akcent, błąd — czerwień, przerwane — ostrzeżenie. */
const STATUS_TONE: Record<RecordRunStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'neutral',
  done: 'accent',
  failed: 'danger',
  interrupted: 'warning',
};

export interface SqlButtonProps {
  /** Napis na przycisku (ustawienie klocka). */
  label: string;
  /** Procedura przycisku — do dopasowania aktywnego i ostatniego zadania. */
  procedureId: string;
  /** Aktywne zadanie rekordu (dowolnej procedury) albo null. */
  active: RecordProcedureRun | null;
  /** Ostatnie zadanie TEJ procedury na rekordzie (stan końcowy lub aktywne) albo undefined. */
  lastRun: RecordProcedureRun | undefined;
  /** Prawo edycji rekordu — ten sam sygnał, który steruje „Edytuj” na karcie. */
  canEdit: boolean;
  /** Trwa wysyłka zlecenia (z dowolnego przycisku tej karty) — wszystkie przyciski nieaktywne. */
  starting: boolean;
  /**
   * Bramka „zapisz przed akcją” formularza w edycji (`useSaveBeforeDecision`); `undefined` = podgląd /
   * brak bramki, zadanie rusza od razu.
   */
  beforeStart?: DecisionGate;
  /**
   * Zleca zadanie tej procedury; strona zgłasza błędy sama (komunikat w rogu) — wynik nie jest tu potrzebny.
   * Odrzucona obietnica (nieprzewidziany wyjątek) jest tylko logowana — przycisk wraca do gotowości.
   */
  onStart: (procedureId: string) => Promise<unknown>;
  /**
   * Prefiks identyfikatorów testowych. Strona z wieloma przyciskami nadaje każdemu własny
   * (np. `sql-button-<id klocka>`), żeby identyfikatory się nie powtarzały.
   */
  testIdPrefix?: string;
}

export function SqlButton({
  label,
  procedureId,
  active,
  lastRun,
  canEdit,
  starting,
  beforeStart,
  onStart,
  testIdPrefix = 'sql-button',
}: SqlButtonProps): ReactElement {
  // Ten przycisk wysyła zlecenie (od startu funkcji do odpowiedzi) — kręciołek tylko na nim.
  const [launching, setLaunching] = useState(false);
  // Ten przycisk czeka na zapis formularza (bramka); kończy się startem zadania albo porażką zapisu.
  const [awaitingSave, setAwaitingSave] = useState(false);

  const gateWaiting = beforeStart?.waiting === true;
  // Zapis nie powiódł się (bramka przestała czekać, a zadanie nie wystartowało) — zdejmij znacznik.
  useEffect(() => {
    if (awaitingSave && !gateWaiting) setAwaitingSave(false);
  }, [awaitingSave, gateWaiting]);

  const busy = launching || (awaitingSave && gateWaiting);
  const activeHere = active !== null && active.procedureId === procedureId;
  // Na rekordzie naraz jedno zadanie: zadanie TEJ ani innej procedury nie pozwala uruchomić kolejnego.
  // Zwykłe „Zapisz” w toku (`saving`) też blokuje — jak przyciski decyzji w banerze procesu.
  const disabled = !canEdit || starting || busy || active !== null || beforeStart?.saving === true;

  function launch(): void {
    setAwaitingSave(false);
    setLaunching(true);
    void onStart(procedureId)
      .catch((err: unknown) => {
        // Strona zgłasza błędy zlecenia sama; odrzucona obietnica oznacza wyjątek, którego nie przewidziała
        // — zostaje ślad w konsoli (zamiast „nieobsłużonego odrzucenia"), a przycisk wraca do gotowości.
        console.error('[SqlButton] start zadania zakończył się wyjątkiem', err);
      })
      .finally(() => setLaunching(false));
  }

  function handleClick(): void {
    if (disabled) return;
    if (beforeStart === undefined) {
      launch();
      return;
    }
    // Bramka zapisu: `false` = nie czeka (zapis zatrzymała walidacja albo kliknięcie zignorowano).
    const waiting = beforeStart(launch);
    if (waiting !== false) setAwaitingSave(true);
  }

  // Wiersz stanu: aktywne zadanie tej procedury ma pierwszeństwo, potem ostatni wynik. Bez prawa
  // edycji sam wyszarzony przycisk (makieta) — stan zadań widać wtedy na pasku blokady rekordu.
  const shown = !canEdit ? undefined : activeHere ? active : lastRun;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testIdPrefix}>
      <Button
        size="sm"
        onClick={handleClick}
        disabled={disabled}
        loading={busy}
        data-testid={`${testIdPrefix}-btn`}
      >
        {label}
      </Button>
      <SqlButtonStatus busy={busy} run={shown} label={label} testIdPrefix={testIdPrefix} />
    </div>
  );
}

interface SqlButtonStatusProps {
  busy: boolean;
  run: RecordProcedureRun | undefined;
  label: string;
  testIdPrefix: string;
}

/**
 * Obok przycisku: „Wykonuję…” w trakcie zlecania, inaczej odznaka + godzina + tekst
 * („Czeka · od 10:02”, „Trwa · od 10:02 · Jan”, „Zakończony · 10:31 · Raport gotowy”).
 */
function SqlButtonStatus({
  busy,
  run,
  label,
  testIdPrefix,
}: SqlButtonStatusProps): ReactElement | null {
  const { t, i18n } = useTranslation();

  if (busy) {
    return (
      <span className="text-[13px] text-text-secondary" data-testid={`${testIdPrefix}-busy`}>
        {t('recordProcedureRuns.button.running')}
      </span>
    );
  }
  if (run === undefined) return null;

  const active = isActiveRunStatus(run.status);
  // Makieta: „Czeka · od 10:02” (bez osoby), „Trwa · od 10:02 · Jan Kowalski”.
  const person = run.status === 'running' ? (run.requestedByName ?? null) : null;
  let text: string;
  if (active) {
    const time = formatRunClock(runSinceIso(run), i18n.language);
    text =
      person !== null
        ? t('recordProcedureRuns.button.sinceBy', { time, person })
        : t('recordProcedureRuns.button.since', { time });
  } else {
    text = t('recordProcedureRuns.button.result', {
      time: formatRunClock(runFinishedIso(run), i18n.language),
      text: runResultText(run, label, t),
    });
  }

  return (
    <>
      <div
        role="status"
        className="flex flex-wrap items-center gap-2 text-[13px] text-text-secondary"
        data-testid={`${testIdPrefix}-status`}
        data-run-status={run.status}
      >
        <Badge tone={STATUS_TONE[run.status]} data-testid={`${testIdPrefix}-badge`}>
          {t(`recordProcedureRuns.status.${run.status}`)}
        </Badge>
        <span data-testid={`${testIdPrefix}-text`}>{text}</span>
      </div>
      {run.status === 'running' ? (
        <p className="basis-full text-xs text-text-muted" data-testid={`${testIdPrefix}-hint`}>
          {t('recordProcedureRuns.button.lockHint')}
        </p>
      ) : null}
      {active && run.attempts > 0 && run.message ? (
        <p className="basis-full text-xs text-text-muted" data-testid={`${testIdPrefix}-note`}>
          {run.message}
        </p>
      ) : null}
    </>
  );
}
